import { hasNativeSensors, collectNativeSensor } from "./native";

const EARTH_RADIUS_M = 6371000;
const BYTE_MAX = 255;
const AUDIO_FFT_SIZE = 2048;
const CAM_W = 64;
const CAM_H = 48;
const CAM_INTERVAL_MS = 400;
const AUDIO_INTERVAL_MS = 100;

function stats(arr) {
  if (!arr || !arr.length) return null;
  let min = Infinity, max = -Infinity, sum = 0;
  for (const v of arr) { if (v < min) min = v; if (v > max) max = v; sum += v; }
  const avg = sum / arr.length;
  let sq = 0;
  for (const v of arr) sq += (v - avg) * (v - avg);
  return { avg, min, max, std: Math.sqrt(sq / arr.length), n: arr.length };
}

const fmt = (x, d = 2) => (x === null || x === undefined || isNaN(x) ? "–" : Number(x).toFixed(d));
const axisLine = (name, s, unit, d = 2) =>
  `${name}: Ø=${fmt(s.avg, d)}, min=${fmt(s.min, d)}, max=${fmt(s.max, d)}, σ=${fmt(s.std, d)} ${unit} (${s.n} Messpunkte)`;

function haversine(lat1, lon1, lat2, lon2) {
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad, dLon = (lon2 - lon1) * rad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
}

async function requestMotionPermission() {
  try {
    if (typeof DeviceMotionEvent !== "undefined" && typeof DeviceMotionEvent.requestPermission === "function") {
      const r = await DeviceMotionEvent.requestPermission();
      if (r !== "granted") throw new Error("Zugriff auf Bewegungssensoren verweigert");
    }
    if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
      await DeviceOrientationEvent.requestPermission().catch(() => {});
    }
  } catch (e) {
    throw new Error("Zugriff auf Bewegungssensoren verweigert");
  }
}

async function collectCamera(durationMs, live) {
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
  } catch (e) {
    throw new Error("Kamerazugriff verweigert oder nicht verfügbar");
  }
  const video = document.createElement("video");
  video.srcObject = stream;
  video.muted = true;
  video.playsInline = true;
  await video.play().catch(() => {});
  const canvas = document.createElement("canvas");
  canvas.width = CAM_W; canvas.height = CAM_H;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const lums = [], rs = [], gs = [], bs = [];
  await new Promise((resolve) => {
    const iv = setInterval(() => {
      try {
        ctx.drawImage(video, 0, 0, CAM_W, CAM_H);
        const d = ctx.getImageData(0, 0, CAM_W, CAM_H).data;
        let r = 0, g = 0, b = 0;
        for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; }
        const px = d.length / 4;
        r /= px; g /= px; b /= px;
        const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        lums.push(lum); rs.push(r); gs.push(g); bs.push(b);
        live(`Helligkeit: ${fmt((lum / BYTE_MAX) * 100, 0)} %`);
      } catch (e) {
        console.warn("Kamera-Frame konnte nicht gelesen werden:", e);
      }
    }, CAM_INTERVAL_MS);
    setTimeout(() => { clearInterval(iv); resolve(); }, durationMs);
  });
  stream.getTracks().forEach((t) => t.stop());
  if (!lums.length) throw new Error("Keine Kamerabilder erfasst");
  const sL = stats(lums), sR = stats(rs), sG = stats(gs), sB = stats(bs);
  const pct = (sL.avg / BYTE_MAX) * 100;
  const cls = pct < 8 ? "sehr dunkel" : pct < 20 ? "dunkel" : pct < 45 ? "mäßig beleuchtet" : pct < 70 ? "hell" : "sehr hell";
  const dom = sR.avg > sG.avg && sR.avg > sB.avg ? "Rot" : sG.avg > sB.avg ? "Grün" : "Blau";
  return {
    technical: [
      axisLine("Bildhelligkeit (Luminanz, 8-Bit 0–255)", sL, ""),
      `Relative Helligkeit: Ø=${fmt(pct, 1)} % des Maximums`,
      `Mittlere Farbkanäle: R=${fmt(sR.avg, 1)}, G=${fmt(sG.avg, 1)}, B=${fmt(sB.avg, 1)} (dominanter Kanal: ${dom})`,
      `Helligkeitsschwankung: max-min=${fmt(sL.max - sL.min, 1)} (8-Bit)`,
    ].join("\n"),
    interpretation: `Szene wirkt ${cls}, dominanter Farbkanal ${dom}.`,
    display: [
      { label: "Ø Helligkeit", value: `${fmt(pct, 0)} %` },
      { label: "Einordnung", value: cls },
      { label: "Dominante Farbe", value: dom },
    ],
  };
}

async function collectMicrophone(durationMs, live) {
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (e) {
    throw new Error("Mikrofonzugriff verweigert oder nicht verfügbar");
  }
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const src = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = AUDIO_FFT_SIZE;
  src.connect(analyser);
  const timeBuf = new Float32Array(analyser.fftSize);
  const freqBuf = new Uint8Array(analyser.frequencyBinCount);
  const dbs = [], freqs = [];
  await new Promise((resolve) => {
    const iv = setInterval(() => {
      analyser.getFloatTimeDomainData(timeBuf);
      let sum = 0;
      for (let i = 0; i < timeBuf.length; i++) sum += timeBuf[i] * timeBuf[i];
      const rms = Math.sqrt(sum / timeBuf.length);
      const db = Math.max(-100, 20 * Math.log10(rms || 1e-5));
      dbs.push(db);
      analyser.getByteFrequencyData(freqBuf);
      let maxV = 0, maxI = 0;
      for (let i = 2; i < freqBuf.length; i++) if (freqBuf[i] > maxV) { maxV = freqBuf[i]; maxI = i; }
      if (maxV > 30) freqs.push((maxI * ctx.sampleRate) / analyser.fftSize);
      live(`Pegel: ${fmt(db, 0)} dBFS`);
    }, AUDIO_INTERVAL_MS);
    setTimeout(() => { clearInterval(iv); resolve(); }, durationMs);
  });
  stream.getTracks().forEach((t) => t.stop());
  ctx.close().catch(() => {});
  if (!dbs.length) throw new Error("Keine Audiodaten erfasst");
  const sD = stats(dbs);
  const peaks = dbs.filter((d) => d > sD.avg + 10).length;
  const sF = freqs.length ? stats(freqs) : null;
  const cls = sD.avg < -60 ? "sehr leise / still" : sD.avg < -40 ? "leise" : sD.avg < -25 ? "mäßig laut" : "laut";
  return {
    technical: [
      axisLine("Schallpegel (dBFS, 0=Vollaussteuerung)", sD, "dBFS", 1),
      `Lautstärkespitzen (> Ø+10 dB): ${peaks} Ereignisse`,
      sF ? axisLine("Dominante Frequenz", sF, "Hz", 0) : "Dominante Frequenz: kein klares Signal erkennbar",
    ].join("\n"),
    interpretation: `Umgebung ist ${cls}${sF ? `, dominante Frequenz um ${fmt(sF.avg, 0)} Hz` : ""}.`,
    display: [
      { label: "Ø Pegel", value: `${fmt(sD.avg, 0)} dBFS` },
      { label: "Einordnung", value: cls },
      { label: "Spitzen", value: `${peaks}` },
    ],
  };
}

async function collectGps(durationMs, live) {
  if (!("geolocation" in navigator)) throw new Error("GPS nicht verfügbar");
  const points = [];
  let watchId;
  await new Promise((resolve, reject) => {
    let errored = false;
    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        points.push({
          lat: pos.coords.latitude, lon: pos.coords.longitude,
          acc: pos.coords.accuracy, alt: pos.coords.altitude,
          speed: pos.coords.speed, t: pos.timestamp,
        });
        live(`Genauigkeit: ±${fmt(pos.coords.accuracy, 0)} m`);
      },
      (err) => {
        if (!errored && err.code === 1) { errored = true; reject(new Error("Standortzugriff verweigert")); }
      },
      { enableHighAccuracy: true, maximumAge: 0 }
    );
    setTimeout(resolve, durationMs);
  }).finally(() => navigator.geolocation.clearWatch(watchId));
  if (!points.length) throw new Error("Keine Positionsdaten empfangen (GPS-Signal?)");
  const accS = stats(points.map((p) => p.acc));
  const alts = points.filter((p) => p.alt !== null).map((p) => p.alt);
  const speeds = points.filter((p) => p.speed !== null && !isNaN(p.speed)).map((p) => p.speed);
  let dist = 0;
  for (let i = 1; i < points.length; i++) dist += haversine(points[i - 1].lat, points[i - 1].lon, points[i].lat, points[i].lon);
  const first = points[0], last = points[points.length - 1];
  const spS = speeds.length ? stats(speeds) : null;
  const altS = alts.length ? stats(alts) : null;
  const moving = dist > Math.max(10, accS.avg);
  return {
    technical: [
      `Startposition: ${fmt(first.lat, 5)}°N, ${fmt(first.lon, 5)}°E | Endposition: ${fmt(last.lat, 5)}°N, ${fmt(last.lon, 5)}°E`,
      `Zurückgelegte Distanz (Summe der Wegpunkte): ${fmt(dist, 1)} m`,
      axisLine("GPS-Genauigkeit", accS, "m", 1),
      altS ? axisLine("Höhe über Meeresspiegel", altS, "m", 1) : "Höhe: nicht verfügbar",
      spS ? axisLine("Geschwindigkeit", spS, "m/s", 2) + ` (Ø ${fmt(spS.avg * 3.6, 1)} km/h)` : "Geschwindigkeit: nicht verfügbar / 0",
    ].join("\n"),
    interpretation: moving
      ? `Gerät hat sich ca. ${fmt(dist, 0)} m bewegt${spS ? ` mit Ø ${fmt(spS.avg * 3.6, 1)} km/h` : ""}.`
      : "Gerät war während der Messung weitgehend ortsfest.",
    display: [
      { label: "Distanz", value: `${fmt(dist, 0)} m` },
      { label: "Genauigkeit", value: `±${fmt(accS.avg, 0)} m` },
      { label: "Status", value: moving ? "in Bewegung" : "ortsfest" },
    ],
  };
}

function motionCollector(extract, buildResult, errMsg) {
  return async (durationMs, live) => {
    await requestMotionPermission();
    const store = {};
    const handler = (e) => extract(e, store, live);
    window.addEventListener("devicemotion", handler);
    await new Promise((r) => setTimeout(r, durationMs));
    window.removeEventListener("devicemotion", handler);
    const result = buildResult(store);
    if (!result) throw new Error(errMsg);
    return result;
  };
}

const collectAccelerometer = motionCollector(
  (e, st, live) => {
    const g = e.accelerationIncludingGravity, a = e.acceleration;
    if (!st.mx) { st.mx = []; st.my = []; st.mz = []; st.mag = []; st.lin = []; }
    if (g && g.x !== null) st.mag.push(Math.sqrt(g.x ** 2 + g.y ** 2 + g.z ** 2));
    if (a && a.x !== null) {
      st.mx.push(a.x); st.my.push(a.y); st.mz.push(a.z);
      const m = Math.sqrt(a.x ** 2 + a.y ** 2 + a.z ** 2);
      st.lin.push(m);
      live(`Beschleunigung: ${fmt(m, 2)} m/s²`);
    }
  },
  (st) => {
    if (!st.lin || !st.lin.length) return null;
    const sx = stats(st.mx), sy = stats(st.my), sz = stats(st.mz), sl = stats(st.lin), sm = stats(st.mag);
    const peaks = st.lin.filter((v) => v > 2).length;
    const cls = sl.avg < 0.15 ? "ruhig / in Ruhe" : sl.avg < 1 ? "leichte Bewegung" : sl.avg < 3 ? "deutliche Bewegung" : "starke Bewegung / Erschütterungen";
    return {
      technical: [
        axisLine("Lineare Beschleunigung X", sx, "m/s²"), axisLine("Lineare Beschleunigung Y", sy, "m/s²"),
        axisLine("Lineare Beschleunigung Z", sz, "m/s²"), axisLine("Betrag lineare Beschleunigung", sl, "m/s²"),
        sm ? axisLine("Betrag inkl. Gravitation (Ruhe ≈ 9,81)", sm, "m/s²") : "",
        `Spitzen > 2 m/s²: ${peaks} Ereignisse`,
      ].filter(Boolean).join("\n"),
      interpretation: `Gerät zeigt ${cls} (Ø ${fmt(sl.avg, 2)} m/s²).`,
      display: [
        { label: "Ø Beschleunigung", value: `${fmt(sl.avg, 2)} m/s²` },
        { label: "Max", value: `${fmt(sl.max, 2)} m/s²` },
        { label: "Einordnung", value: cls },
      ],
    };
  },
  "Keine Beschleunigungsdaten empfangen (Sensor evtl. nicht vorhanden)"
);

const collectGyroscope = motionCollector(
  (e, st, live) => {
    const r = e.rotationRate;
    if (!st.a) { st.a = []; st.b = []; st.g = []; st.m = []; }
    if (r && r.alpha !== null) {
      st.a.push(r.alpha); st.b.push(r.beta); st.g.push(r.gamma);
      const m = Math.sqrt(r.alpha ** 2 + r.beta ** 2 + r.gamma ** 2);
      st.m.push(m);
      live(`Drehrate: ${fmt(m, 1)} °/s`);
    }
  },
  (st) => {
    if (!st.m || !st.m.length) return null;
    const sa = stats(st.a), sb = stats(st.b), sg = stats(st.g), sm = stats(st.m);
    const cls = sm.avg < 2 ? "keine nennenswerte Rotation" : sm.avg < 30 ? "leichte Drehbewegungen" : "starke Drehbewegungen";
    return {
      technical: [
        axisLine("Drehrate α (Z-Achse)", sa, "°/s", 1), axisLine("Drehrate β (X-Achse)", sb, "°/s", 1),
        axisLine("Drehrate γ (Y-Achse)", sg, "°/s", 1), axisLine("Betrag der Drehrate", sm, "°/s", 1),
      ].join("\n"),
      interpretation: `${cls} (Ø ${fmt(sm.avg, 1)} °/s, max ${fmt(sm.max, 1)} °/s).`,
      display: [
        { label: "Ø Drehrate", value: `${fmt(sm.avg, 1)} °/s` },
        { label: "Max", value: `${fmt(sm.max, 1)} °/s` },
        { label: "Einordnung", value: cls },
      ],
    };
  },
  "Keine Gyroskopdaten empfangen (Sensor evtl. nicht vorhanden)"
);

function luxResult(values) {
  const s = stats(values);
  const cls = s.avg < 10 ? "sehr dunkel" : s.avg < 50 ? "dunkel/gedimmt" : s.avg < 400 ? "Innenraumbeleuchtung" : s.avg < 2000 ? "hell" : "Tageslicht";
  return {
    technical: axisLine("Beleuchtungsstärke", s, "Lux", 1),
    interpretation: `Beleuchtung entspricht: ${cls} (Ø ${fmt(s.avg, 0)} Lux).`,
    display: [
      { label: "Ø Licht", value: `${fmt(s.avg, 0)} Lux` },
      { label: "Einordnung", value: cls },
    ],
  };
}

async function collectProximity(durationMs, live) {
  if (!hasNativeSensors()) throw new Error("Näherungssensor im Browser nicht verfügbar – nutze die Android-App.");
  const values = await collectNativeSensor("proximity", durationMs, (v) => live(`${fmt(v, 1)} cm`));
  if (!values.length) throw new Error("Keine Daten vom Näherungssensor empfangen");
  const s = stats(values);
  const nearThreshold = Math.max(1, s.max / 2);
  const nearCount = values.filter((v) => v < nearThreshold).length;
  const nearPct = (nearCount / values.length) * 100;
  let transitions = 0;
  for (let i = 1; i < values.length; i++) {
    if (values[i - 1] < nearThreshold !== values[i] < nearThreshold) transitions++;
  }
  return {
    technical: [
      axisLine("Abstand zum Objekt", s, "cm", 1),
      `Anteil „nah“ (< ${fmt(nearThreshold, 1)} cm): ${fmt(nearPct, 0)} %`,
      `Nah/Fern-Wechsel: ${transitions}`,
    ].join("\n"),
    interpretation:
      nearPct > 50
        ? `Während der Messung war meist ein Objekt nah am Sensor (${fmt(nearPct, 0)} % der Zeit).`
        : `Der Sensor war überwiegend frei (${fmt(100 - nearPct, 0)} % der Zeit kein Objekt nah).`,
    display: [
      { label: "Ø Abstand", value: `${fmt(s.avg, 1)} cm` },
      { label: "Nah-Anteil", value: `${fmt(nearPct, 0)} %` },
    ],
  };
}

async function collectTemperature(durationMs, live) {
  if (!hasNativeSensors()) throw new Error("Temperatursensor im Browser nicht verfügbar – nutze die Android-App.");
  const values = await collectNativeSensor("temperature", durationMs, (v) => live(`${fmt(v, 1)} °C`));
  if (!values.length) throw new Error("Kein Temperatursensor auf diesem Gerät vorhanden");
  const s = stats(values);
  const cls = s.avg < 10 ? "kalt" : s.avg < 18 ? "kühl" : s.avg < 26 ? "angenehm" : s.avg < 32 ? "warm" : "heiß";
  return {
    technical: axisLine("Umgebungstemperatur", s, "°C", 1),
    interpretation: `Umgebungstemperatur wirkt ${cls} (Ø ${fmt(s.avg, 1)} °C).`,
    display: [
      { label: "Ø Temperatur", value: `${fmt(s.avg, 1)} °C` },
      { label: "Einordnung", value: cls },
    ],
  };
}

async function collectLight(durationMs, live) {
  if (hasNativeSensors()) {
    try {
      const values = await collectNativeSensor("light", durationMs, (v) => live(`${fmt(v, 0)} Lux`));
      if (values.length) return luxResult(values);
    } catch (e) {
      console.warn("Nativer Lichtsensor fehlgeschlagen, Fallback wird versucht:", e);
    }
  }
  if ("AmbientLightSensor" in window) {
    try {
      const values = [];
      const sensor = new window.AmbientLightSensor({ frequency: 5 });
      await new Promise((resolve, reject) => {
        sensor.addEventListener("reading", () => {
          values.push(sensor.illuminance);
          live(`${fmt(sensor.illuminance, 0)} Lux`);
        });
        sensor.addEventListener("error", (e) => reject(e.error));
        sensor.start();
        setTimeout(resolve, durationMs);
      });
      sensor.stop();
      if (values.length) {
        const s = stats(values);
        const cls = s.avg < 10 ? "sehr dunkel" : s.avg < 50 ? "dunkel/gedimmt" : s.avg < 400 ? "Innenraumbeleuchtung" : s.avg < 2000 ? "hell" : "Tageslicht";
        return {
          technical: axisLine("Beleuchtungsstärke", s, "Lux", 1),
          interpretation: `Beleuchtung entspricht: ${cls} (Ø ${fmt(s.avg, 0)} Lux).`,
          display: [
            { label: "Ø Licht", value: `${fmt(s.avg, 0)} Lux` },
            { label: "Einordnung", value: cls },
          ],
        };
      }
    } catch (e) {
      console.warn("AmbientLightSensor fehlgeschlagen, Kamera-Fallback:", e);
    }
  }
  const cam = await collectCamera(durationMs, live);
  return {
    technical: `Hinweis: Kein dedizierter Lichtsensor im Browser verfügbar – Schätzung über Kamerahelligkeit.\n${cam.technical}`,
    interpretation: `${cam.interpretation} (Schätzung über Kamera)`,
    display: cam.display,
  };
}

async function collectMagnetometer(durationMs, live) {
  if ("Magnetometer" in window) {
    try {
      const mags = [];
      const sensor = new window.Magnetometer({ frequency: 10 });
      await new Promise((resolve, reject) => {
        sensor.addEventListener("reading", () => {
          const m = Math.sqrt(sensor.x ** 2 + sensor.y ** 2 + sensor.z ** 2);
          mags.push(m);
          live(`${fmt(m, 1)} µT`);
        });
        sensor.addEventListener("error", (e) => reject(e.error));
        sensor.start();
        setTimeout(resolve, durationMs);
      });
      sensor.stop();
      if (mags.length) {
        const s = stats(mags);
        const anomaly = s.avg > 100 || s.avg < 15;
        return {
          technical: axisLine("Magnetfeldstärke (Betrag)", s, "µT", 1) + "\nErdmagnetfeld typ.: 25–65 µT",
          interpretation: anomaly ? `Auffälliges Magnetfeld (Ø ${fmt(s.avg, 1)} µT) – evtl. Magnet/Metall/Elektronik in der Nähe.` : `Magnetfeld im normalen Bereich des Erdmagnetfelds (Ø ${fmt(s.avg, 1)} µT).`,
          display: [
            { label: "Ø Feldstärke", value: `${fmt(s.avg, 1)} µT` },
            { label: "Einordnung", value: anomaly ? "auffällig" : "normal" },
          ],
        };
      }
    } catch (e) {
      console.warn("Magnetometer fehlgeschlagen, Kompass-Fallback:", e);
    }
  }
  const headings = [];
  const handler = (e) => {
    let h = null;
    if (typeof e.webkitCompassHeading === "number") h = e.webkitCompassHeading;
    else if (e.absolute && e.alpha !== null) h = 360 - e.alpha;
    else if (e.alpha !== null) h = 360 - e.alpha;
    if (h !== null) { h = ((h % 360) + 360) % 360; headings.push(h); live(`${fmt(h, 0)}°`); }
  };
  if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
    await DeviceOrientationEvent.requestPermission().catch(() => {});
  }
  window.addEventListener("deviceorientationabsolute", handler);
  window.addEventListener("deviceorientation", handler);
  await new Promise((r) => setTimeout(r, durationMs));
  window.removeEventListener("deviceorientationabsolute", handler);
  window.removeEventListener("deviceorientation", handler);
  if (!headings.length) throw new Error("Keine Kompassdaten empfangen (Sensor evtl. nicht vorhanden)");
  const s = stats(headings);
  const dirs = ["Nord", "Nordost", "Ost", "Südost", "Süd", "Südwest", "West", "Nordwest"];
  const dir = dirs[Math.round(s.avg / 45) % 8];
  return {
    technical: axisLine("Kompasskurs (0°=Nord, über Magnetfeld)", s, "°", 1),
    interpretation: `Gerät zeigt im Mittel Richtung ${dir} (${fmt(s.avg, 0)}°).`,
    display: [
      { label: "Ø Kurs", value: `${fmt(s.avg, 0)}°` },
      { label: "Richtung", value: dir },
    ],
  };
}

async function collectOrientation(durationMs, live) {
  if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
    const r = await DeviceOrientationEvent.requestPermission().catch(() => "denied");
    if (r !== "granted") throw new Error("Zugriff auf Lagesensoren verweigert");
  }
  const al = [], be = [], ga = [];
  const handler = (e) => {
    if (e.alpha === null && e.beta === null) return;
    if (e.alpha !== null) al.push(e.alpha);
    if (e.beta !== null) be.push(e.beta);
    if (e.gamma !== null) ga.push(e.gamma);
    live(`Neigung: ${fmt(e.beta, 0)}° / ${fmt(e.gamma, 0)}°`);
  };
  window.addEventListener("deviceorientation", handler);
  await new Promise((r) => setTimeout(r, durationMs));
  window.removeEventListener("deviceorientation", handler);
  if (!be.length) throw new Error("Keine Lagedaten empfangen (Sensor evtl. nicht vorhanden)");
  const sa = al.length ? stats(al) : null, sb = stats(be), sg = stats(ga);
  let posture = "unbestimmte Lage";
  if (Math.abs(sb.avg) < 20 && Math.abs(sg.avg) < 20) posture = "liegt annähernd flach";
  else if (sb.avg > 55 && sb.avg < 125) posture = "wird aufrecht gehalten";
  else if (Math.abs(sg.avg) > 55) posture = "liegt seitlich / quer";
  else posture = "wird geneigt gehalten";
  const stable = sb.std < 3 && sg.std < 3;
  return {
    technical: [
      sa ? axisLine("Kompasswinkel α (0–360°)", sa, "°", 1) : "α: nicht verfügbar",
      axisLine("Neigung β (vor/zurück, -180–180°)", sb, "°", 1),
      axisLine("Neigung γ (links/rechts, -90–90°)", sg, "°", 1),
    ].join("\n"),
    interpretation: `Gerät ${posture} und ist dabei ${stable ? "ruhig/stabil" : "in Bewegung"}.`,
    display: [
      { label: "Lage", value: posture },
      { label: "Stabilität", value: stable ? "stabil" : "bewegt" },
    ],
  };
}

const COLLECTORS = {
  camera: collectCamera,
  microphone: collectMicrophone,
  gps: collectGps,
  accelerometer: collectAccelerometer,
  gyroscope: collectGyroscope,
  proximity: collectProximity,
  light: collectLight,
  magnetometer: collectMagnetometer,
  temperature: collectTemperature,
  orientation: collectOrientation,
};

const hasMedia = () => !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

export const SENSOR_DEFS = [
  { id: "camera", name: "Kamera / Bildanalyse", icon: "Camera", desc: "Misst Helligkeit und Farbverteilung der Umgebung über die Kamera.", use: "Lichtverhältnisse prüfen, Umgebung einschätzen", available: hasMedia },
  { id: "microphone", name: "Mikrofon / Schallanalyse", icon: "Microphone", desc: "Misst Lautstärkepegel und dominante Frequenzen des Umgebungsschalls.", use: "Lärm bewerten, Geräuschquellen einordnen", available: hasMedia },
  { id: "gps", name: "Standort / GPS", icon: "MapPin", desc: "Erfasst Position, Höhe, Geschwindigkeit und Bewegungsdistanz.", use: "Bewegung, Strecke und Ortskontext analysieren", available: () => "geolocation" in navigator },
  { id: "accelerometer", name: "Bewegung / Beschleunigung", icon: "Gauge", desc: "Misst Beschleunigungskräfte in x, y, z (m/s²).", use: "Vibrationen, Schritte, Fahrten analysieren", available: () => typeof DeviceMotionEvent !== "undefined" },
  { id: "gyroscope", name: "Rotation / Gyroskop", icon: "ArrowsClockwise", desc: "Misst Drehraten um drei Achsen (°/s).", use: "Drehbewegungen und Stabilität untersuchen", available: () => typeof DeviceMotionEvent !== "undefined" },
  { id: "proximity", name: "Annäherung / Näherungssensor", icon: "Ruler", desc: "Erkennt, ob sich ein Objekt nah am Display befindet.", use: "Abstandserkennung (z. B. Ohr am Telefon)", available: hasNativeSensors, note: "Im Browser nicht verfügbar – in der Android-App (nativ) nutzbar." },
  { id: "light", name: "Licht / Umgebungslicht", icon: "Sun", desc: "Misst die Beleuchtungsstärke der Umgebung (Lux bzw. Kamera-Schätzung).", use: "Helligkeit bewerten, Arbeitsplatzlicht prüfen", available: hasMedia },
  { id: "magnetometer", name: "Magnetfeld / Kompass", icon: "Compass", desc: "Misst das Magnetfeld bzw. die Kompassrichtung des Geräts.", use: "Himmelsrichtung, magnetische Störquellen finden", available: () => typeof DeviceOrientationEvent !== "undefined" || "Magnetometer" in window },
  { id: "temperature", name: "Temperatur", icon: "Thermometer", desc: "Misst die Umgebungstemperatur, falls dein Gerät den Sensor besitzt.", use: "Thermische Einschätzung der Umgebung", available: hasNativeSensors, note: "Im Browser nicht verfügbar – in der Android-App (nativ) nutzbar, sofern dein Gerät den Sensor hat." },
  { id: "orientation", name: "Ausrichtung / Gerätelage", icon: "DeviceMobile", desc: "Kombinierte Lagedaten: Neigung und Drehung des Geräts im Raum.", use: "Wie wird das Gerät gehalten? Wasserwaage & Co.", available: () => typeof DeviceOrientationEvent !== "undefined" },
];

export async function runMeasurement(ids, durationSec, onLive) {
  const start = new Date();
  const results = await Promise.all(
    ids.map(async (id) => {
      const def = SENSOR_DEFS.find((d) => d.id === id);
      try {
        const r = await COLLECTORS[id](durationSec * 1000, (text) => onLive(id, text));
        return { id, name: def.name, ...r };
      } catch (e) {
        return { id, name: def.name, error: e.message || "Messung fehlgeschlagen" };
      }
    })
  );
  const end = new Date();
  return {
    window: { start: start.toISOString(), end: end.toISOString(), duration: durationSec },
    sensors: results,
  };
}
